import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import FormInput from '../components/FormInput';
import PasswordInput from '../components/PasswordInput';
import FormButton from '../components/FormButton';
import ValidationMessage from '../components/ValidationMessage';
import FuturaSmileIcon from '../components/icons/FuturaSmileIcon';
import ChevronLeftIcon from '../components/icons/ChevronLeftIcon';
import { validateEmail } from '../utils/validation';
import { getReferralCode, clearReferralCode } from '../utils/referralUtils';
import toast from 'react-hot-toast';
import OtpInput from '../components/OtpInput';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailValidation, setEmailValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [emailError, setEmailError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Resend Verification State
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // MFA State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const { login, loginWithGoogle, validateMFA, error: authError, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Google OAuth login handler
  const loginToGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Retrieve referral code if present (in case this is a new user signup via Google)
        const referralCode = getReferralCode();

        const result = await loginWithGoogle(tokenResponse.access_token, referralCode || undefined);

        if (result && (result as any).mfaSetupRequired) {
          toast('MFA Setup Required', { icon: '🛡️' });
          navigate('/mfa-setup', {
            state: { tempToken: (result as any).tempToken },
            replace: true
          });
        } else if (result && (result as any).mfaRequired) {
          setTempToken((result as any).tempToken);
          setMfaRequired(true);
        } else {
          // Clear referral code after successful authentication
          clearReferralCode();

          toast.success('Welcome back!');
          navigate(from, { replace: true });
        }
      } catch (err: any) {
        console.error('Google login error:', err);
        toast.error(err.message || 'Google login failed');
      }
    },
    onError: () => {
      toast.error('Google login failed');
    }
  });

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (email) {
      const validation = validateEmail(email);
      setEmailValidation(validation.valid ? 'valid' : 'invalid');
      setEmailError(validation.error || '');
    } else {
      setEmailValidation('idle');
      setEmailError('');
    }
  }, [email]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;

    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        toast.success('Verification email sent!');
        setResendCooldown(60);
      } else {
        toast.error('Failed to send verification email');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowResendVerification(false); // Reset on new attempt

    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error || '');
      setEmailValidation('invalid');
      return;
    }

    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await login(email, password);

      if (result && result.mfaRequired) {
        setMfaRequired(true);
        setTempToken(result.tempToken);
        toast('Please enter your MFA code', { icon: '🔒' });
      } else if (result && (result as any).mfaSetupRequired) {
        // Enforce MFA setup
        toast('MFA Setup Required', { icon: 'shield' });
        navigate('/mfa-setup', {
          state: { tempToken: (result as any).tempToken },
          replace: true
        });
      } else {
        toast.success('Welcome back!');
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      console.error('Login error:', err);
      toast.error(err.message || 'Login failed');

      if (err.message && (err.message.includes('verify your email') || err.message.includes('unverified'))) {
        setShowResendVerification(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode || mfaCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setIsSubmitting(true);
    try {
      await validateMFA(tempToken, mfaCode);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('MFA error:', err);
      toast.error(err.message || 'Invalid code');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex">
      {/* Decorative Branding Panel - Hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/sign-in/background.png)'
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col justify-between p-8 h-full">
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-3">
              <img
                src="/images/Logo.webp"
                alt="FuturaSmile Logo"
                className="h-8 w-auto object-contain"
              />
            </div>
            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ x: -2 }}
              className="flex items-center space-x-2 px-4 py-2 rounded-full border border-gray-300 text-gray-200 hover:bg-white/10 transition-all duration-300"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Back to website</span>
            </motion.button>
          </div>
          <div className="max-w-md">
            <blockquote className="text-2xl font-serif text-white leading-relaxed">
              "Every future smile starts with one decisive moment. Make it yours."
            </blockquote>
          </div>
        </div>
      </div>

      {/* Authentication Form Panel */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="text-3xl font-bold text-gray-200 mb-2">
                {mfaRequired ? 'Two-Factor Authentication' : 'Welcome Back!'}
              </h1>
              <p className="text-gray-400">
                {mfaRequired
                  ? 'Please enter the code from your authenticator app.'
                  : (
                    <>
                      Don't have an account?{' '}
                      <Link
                        to="/register"
                        className="font-medium text-teal-400 hover:text-teal-300 transition-colors"
                      >
                        Sign up
                      </Link>
                    </>
                  )
                }
              </p>
            </motion.div>
          </div>

          {mfaRequired ? (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
              onSubmit={handleMFAVerify}
            >
              <div className="flex flex-col items-center gap-4">
                <label className="text-gray-300 text-sm font-medium">Authentication Code</label>
                <OtpInput
                  value={mfaCode}
                  onChange={setMfaCode}
                  length={6}
                  disabled={isLoading || isSubmitting}
                />
                <p className="text-xs text-gray-500">Enter the 6-digit code from your app</p>
              </div>

              {authError && (
                <ValidationMessage
                  type="error"
                  message={authError}
                />
              )}

              <div className="flex gap-3">
                <FormButton
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaCode('');
                    setTempToken('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </FormButton>
                <FormButton
                  type="submit"
                  loading={isLoading || isSubmitting}
                  disabled={isLoading || isSubmitting}
                  className="flex-1"
                  variant="teal"
                >
                  Verify
                </FormButton>
              </div>
            </motion.form>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
              onSubmit={handleSubmit}
            >
              <FormInput
                id="email"
                name="email"
                type="email"
                label="Email address"
                value={email}
                onChange={setEmail}
                placeholder="Enter your email"
                required
                autoComplete="email"
                validationState={emailValidation}
                error={emailError}
              />

              <PasswordInput
                id="password"
                name="password"
                label="Password"
                value={password}
                onChange={setPassword}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-teal-400 hover:text-teal-300 transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>

              {authError && (
                <div className="flex flex-col gap-2">
                  <ValidationMessage
                    type="error"
                    message={authError}
                  />
                  {showResendVerification && (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendCooldown > 0}
                      className={`text-sm text-teal-400 hover:text-teal-300 underline text-left ${resendCooldown > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {resendCooldown > 0 ? `Resend available in ${resendCooldown}s` : 'Resend verification email'}
                    </button>
                  )}
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FormButton
                  type="submit"
                  loading={isLoading || isSubmitting}
                  disabled={isLoading || isSubmitting}
                  fullWidth
                  size="lg"
                  variant="teal"
                >
                  {isLoading || isSubmitting ? 'Signing in...' : 'Log in'}
                </FormButton>
              </motion.div>
            </motion.form>
          )}

          {!mfaRequired && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-8"
              >
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-zinc-900 text-gray-400">Or continue with</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-6 flex justify-center"
              >
                <button
                  type="button"
                  onClick={() => loginToGoogle()}
                  className="flex items-center justify-center w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-200 hover:bg-zinc-800 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Google
                </button>
              </motion.div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;